import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Handshake, LogOut, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Reveal";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "Comment ça marche", id: "comment" },
  { label: "Coach civique", id: "coach" },
  { label: "Assistant démarches", id: "assistant" },
  { label: "Parcours d'intégration", id: "parcours" },
  { label: "Ressources", id: "ressources" },
];

export const scrollToId = (id) => (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -84, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

function InterpreteLink() {
  return (
    <Link
      to="/dashboard/interprete"
      className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-bleu/40 hover:text-bleu"
      data-testid="navbar-interprete-link"
    >
      <span aria-hidden>🌐</span> Interprète
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success("Vous êtes déconnecté. À bientôt !");
    navigate("/");
  };

  return (
    <header className="glass-header fixed inset-x-0 top-0 z-50">
      <div className="h-0.5 w-full bg-gradient-to-r from-bleu via-white to-rouge" />
      <div className="container-integra flex h-[76px] items-center justify-between gap-4 2xl:max-w-[96rem]">
        <Link to="/" aria-label="INTEGRA FRANCE — Accueil" className="shrink-0">
          <Logo compact />
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" aria-label="Navigation principale">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={scrollToId(l.id)}
              className="whitespace-nowrap text-[13px] font-medium text-slate-600 transition-colors hover:text-bleu"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/devenir-partenaire"
            className="flex items-center gap-1.5 whitespace-nowrap text-[13px] font-bold text-bleu transition-colors hover:text-bleu-dark"
            data-testid="navbar-partner-link"
          >
            <Handshake className="h-3.5 w-3.5 shrink-0" /> Devenir partenaire
          </Link>
        </nav>

        <div className="hidden shrink-0 items-center gap-2.5 xl:flex">
          <span className="hidden 2xl:block"><InterpreteLink /></span>
          {user ? (
            <>
              <Link to="/dashboard" className="flex items-center gap-2 rounded-full bg-bleu-light px-4 py-2 text-xs font-semibold text-bleu transition-colors hover:bg-bleu hover:text-white" data-testid="navbar-dashboard-link">
                <UserRound className="h-3.5 w-3.5" /> Mon espace
              </Link>
              <button onClick={handleLogout} className="btn-secondary !px-5 !py-2.5" data-testid="navbar-logout-button">
                <LogOut className="h-4 w-4" /> Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="whitespace-nowrap rounded-full px-3.5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:text-bleu" data-testid="navbar-login-button">
                Se connecter
              </Link>
              <Link to="/signup" className="btn-primary whitespace-nowrap !px-5 !py-2.5" data-testid="navbar-signup-button">
                <span className="hidden 2xl:inline">Commencer mon parcours</span>
                <span className="2xl:hidden">Commencer</span>
              </Link>
            </>
          )}
        </div>

        <button
          className="rounded-xl border border-slate-200 p-2.5 text-slate-700 xl:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Ouvrir le menu"
          data-testid="navbar-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-slate-100 bg-white xl:hidden"
          >
            <div className="container-integra flex flex-col gap-1 py-5">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={(e) => {
                    scrollToId(l.id)(e);
                    setOpen(false);
                  }}
                  className="rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-bleu-light hover:text-bleu"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/devenir-partenaire"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-xl bg-bleu-light px-4 py-3 text-sm font-bold text-bleu"
                data-testid="navbar-partner-mobile"
              >
                <Handshake className="h-4 w-4" /> Devenir partenaire
              </Link>
              <div className="mt-3 flex flex-col gap-2.5 border-t border-slate-100 pt-4">
                <InterpreteLink />
                {user ? (
                  <>
                    <Link to="/dashboard" className="btn-primary w-full" data-testid="navbar-dashboard-mobile">Mon espace</Link>
                    <button onClick={handleLogout} className="btn-secondary w-full" data-testid="navbar-logout-mobile">
                      Se déconnecter
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="btn-secondary w-full" data-testid="navbar-login-mobile">Se connecter</Link>
                    <Link to="/signup" className="btn-primary w-full" data-testid="navbar-signup-mobile">Commencer mon parcours</Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
